import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { WebUI, getNid } from "../../wui-core/src/index";
import Icon from '../../wui-icon/src';
import Tooltip from '../../wui-tooltip/src';
import { TooltipPlacement } from '../../wui-tooltip/src/iTooltip';
import i18n from './i18n.js';
import {getLangInfo} from "../../wui-locale/src/tool";
import {WithConfigConsumer} from "../../wui-provider/src/context";

import Pane from './SpliterPane';
import Resizer from './SpliterResizer';
import { SpliterProps } from './iLayout';

const defaultProps: SpliterProps = {
    minSize: 50,
    primary: 'first',
    direction: 'vertical',
    allowResize: true,
    defaultMode: 'default',
    resizerable: true,
    // 目前不对外抛出
    step: 5,
};

function unFocus(document: any, window: any) {
    if (document.selection) {
        document.selection.empty();
    } else {
        try {
            window.getSelection().removeAllRanges();
        } catch (e) {}
    }
}

function getSize(props: SpliterProps, collapsed: boolean) {
    let newSize =
    props.size ??
    props.defaultSize ??
    props.minSize;
    newSize = collapsed ? 0 : newSize;
    return newSize;
}

const Spliter: React.FC<SpliterProps> = (props) => {
    const {
        allowResize = true,
        children,
        className,
        clsPrefix,
        defaultSize,
        minSize = 50,
        onResizerClick,
        onResizerDoubleClick,
        primary = 'first',
        resizerClassName,
        resizerStyle,
        direction = 'vertical',
        collapsible,
        style: styleProps,
        resizerable = true,
        fieldid,
        onDragStarted,
        onDragFinished,
        onDragMove,
        trigger,
        triggerStyle,
        locale,
        dir,
        defaultMode = 'default',
        step = 5,
        onCollapse,
        uirunmode,
        maxSize,
        mode: modeProps
    } = props;

    const initialCollapsed = props.collapsed ?? props.defaultCollapsed ?? false;
    const currentSize = getSize(props, initialCollapsed);

    const [active, setActive] = useState(false);
    const [resized, setResized] = useState(false);
    const [position, setPosition] = useState(0);
    const [draggedSize, setDraggedSize] = useState<number | string | null>(currentSize);
    const [size, setSize] = useState(currentSize);
    const [collapsed, setCollapsed] = useState(initialCollapsed);
    const [mode, setMode] = useState(modeProps ? modeProps === 'mixed' ? defaultMode : modeProps : defaultMode);

    const spliterRef = useRef<HTMLDivElement | null>(null);
    const pane1Ref = useRef<HTMLDivElement | null>(null);
    const pane2Ref = useRef<HTMLDivElement | null>(null);
    const pane1Key = useRef(new Date().getTime().toString());
    const pane2Key = useRef((new Date().getTime() + 1).toString());
    const resizerRef = useRef<any>(null);
    const langInfo = getLangInfo(locale, i18n, 'layout');

    // Handle props changes
    useEffect(() => {
        if (modeProps && modeProps !== 'mixed') {
            setMode(modeProps);
        } else if (modeProps === 'mixed') {
            setMode(defaultMode);
        }
    }, [modeProps, defaultMode]);

    useEffect(() => {
        if ('collapsed' in props && props.collapsed !== undefined) {
            setCollapsed(props.collapsed);
            setSize(getSize(props, props.collapsed));
        }
    }, [props.collapsed]);

    useEffect(() => {
        if ('size' in props && props.size !== undefined) {
            if (props.size == 0 && resized) {
                setCollapsed(true);
            }
            setSize(props.size);
        }
    }, [props.size, resized]);

    const onTouchStart = useCallback((event: any) => {
        unFocus(document, window);
        // 记录开始位置
        const newPosition =
        direction === 'vertical'
            ? event.touches[0].clientX
            : event.touches[0].clientY;

        if (typeof onDragStarted === 'function') {
            onDragStarted();
        }
        setActive(true);
        setPosition(newPosition);
    }, [direction, onDragStarted]);

    const onMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
        if (uirunmode == 'design') return; // 设计态禁止拖拽
        if (!resizerable) {
            return;
        }
        const eventWithTouches = {
            ...event,
            touches: [{ clientX: event.clientX, clientY: event.clientY }],
        };
        onTouchStart(eventWithTouches);
    }, [uirunmode, resizerable, onTouchStart]);

    const onTouchMove = useCallback((event: MouseEvent & { touches: { clientX: number; clientY: number; }[]; }) => {
        if (allowResize && active) {
            unFocus(document, window);
            const isPrimaryFirst = primary === 'first';
            const domKey = isPrimaryFirst ? `pane1${pane1Key.current}` : `pane2${pane2Key.current}`;
            const node = document.querySelector(`div[data-domId="${domKey}"]`) as Element;

            if (node?.getBoundingClientRect) {
                const width = node.getBoundingClientRect().width;
                const height = node.getBoundingClientRect().height;
                // 当前鼠标移动位置
                const current =
                    direction === 'vertical'
                        ? event.touches[0].clientX
                        : event.touches[0].clientY;
                const currentSize = direction === 'vertical' ? width : height;
                let positionDelta = position! - current;
                if (step) {
                    if (Math.abs(positionDelta) < step) {
                        return;
                    }
                    // Integer division
                    // eslint-disable-next-line no-bitwise
                    positionDelta = ~~(positionDelta / step) * step;
                }
                let sizeDelta = isPrimaryFirst ? positionDelta : -positionDelta;

                const minSizeNum = Number(minSize) as number;
                let maxSizeNum = maxSize;
                if (maxSizeNum !== undefined && Number(maxSizeNum) && Number(maxSizeNum) <= 0) {
                    const splPane = spliterRef.current as HTMLDivElement;
                    if (direction === 'vertical') {
                        maxSizeNum = splPane.getBoundingClientRect().width + Number(maxSize);
                    } else {
                        maxSizeNum = splPane.getBoundingClientRect().height + Number(maxSize);
                    }
                }

                let newSize = direction === 'vertical' && dir === 'rtl' ? currentSize + sizeDelta : currentSize - sizeDelta;
                const newPosition = position - positionDelta;


                if (newSize < minSizeNum) {
                    newSize = minSizeNum as number;
                } else if (maxSizeNum !== undefined && Number(maxSizeNum) && newSize > Number(maxSizeNum)) {
                    newSize = maxSizeNum as number;
                } else {
                    setPosition(newPosition);
                    setResized(true);
                }

                if (onDragMove) onDragMove(newSize);
                // 从0拖拽展开状态改变
                if (newSize > 0 && resized) {
                    setCollapsed(false);
                }
                setDraggedSize(newSize);
                if (typeof props?.size !== 'number') {
                    setSize(newSize);
                }
            }
        }
    }, [allowResize, active, primary, direction, position, step, maxSize, dir, minSize, onDragMove, resized, props.size]);

    const onMouseMove = useCallback((event: MouseEvent) => {
        const eventWithTouches = Object.assign({}, event, {
            touches: [{ clientX: event.clientX, clientY: event.clientY }],
        });
        onTouchMove(eventWithTouches);
    }, [onTouchMove]);

    const onMouseUp = useCallback(() => {
        if (allowResize && active) {
            if (typeof onDragFinished === 'function') {
                onDragFinished(draggedSize!);
            }
            setActive(false);
        }
    }, [allowResize, active, onDragFinished, draggedSize]);

    // Add and remove event listeners
    useEffect(() => {
        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('touchmove', onTouchMove);

        return () => {
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('touchmove', onTouchMove);
        };
    }, [onMouseUp, onMouseMove, onTouchMove]);

    const handleChangeCollapsed = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>, value: boolean) => {
        e.stopPropagation();
        if (!('collapsed' in props)) {
            setCollapsed(value);
            const newSize = getSize(props, value);
            setSize(newSize);
            setDraggedSize(newSize);
        }
        onCollapse && onCollapse(value);
    }, [props, onCollapse]);

    const handleFixedNailClick = useCallback(() => {
        setMode(prevMode => prevMode === 'on' ? 'default' : 'on');
    }, []);

    const getTriggerDom = useCallback(() => {
        const directionFinal = direction === 'vertical' || typeof direction === undefined || direction === 'horizontal' ? direction : defaultProps.direction;
        const verticalReverseArrow = (primary === 'second');
        const horizontalReverseArrow = primary === 'second';
        const verticalReversePos = (primary === 'second' && dir !== 'rtl') || (dir === 'rtl' && primary === 'first');
        const horizontalReversePos = primary === 'second';
        const iconObj = {
            verticalExpanded: verticalReverseArrow ? <Icon type={ "uf-triangle-right"} /> : <Icon type={"uf-triangle-left"} />,
            verticalCollapsed: verticalReverseArrow ? <Icon type={ "uf-triangle-left"} /> : <Icon type={ "uf-triangle-right"} />,
            horizontalExpanded: horizontalReverseArrow ? <Icon type="uf-zhankai" /> : <Icon type="uf-shouqi" />,
            horizontalCollapsed: horizontalReverseArrow ? <Icon type="uf-shouqi" /> : <Icon type="uf-zhankai" />,
        };
        const styleObj = {
            verticalExpanded: verticalReversePos ? {'left': '1px', 'right': 'unset', borderRadius: '0 50% 50% 0'} : {'left': 'unset', 'right': '1px', borderRadius: '50% 0% 0% 50%'},
            verticalCollapsed: verticalReversePos ? {'right': '0', 'left': 'unset', borderRadius: '50% 0 0 50%'} : {'left': '0px', 'right': 'unset', borderRadius: '0 50% 50% 0'},
            horizontalExpanded: horizontalReversePos ? {top: '1px', bottom: 'unset', borderRadius: '0 0 50% 50%'} : {top: 'unset', bottom: '1px', borderRadius: '50% 50% 0 0'},
            horizontalCollapsed: horizontalReversePos ? {top: 'unset', bottom: '1px', borderRadius: '50% 50% 0 0'} : {top: '1px', bottom: 'unset', borderRadius: '0 0 50% 50%'},
        };
        const status = collapsed ? `${directionFinal}Collapsed` : `${directionFinal}Expanded`;
        const defaultTrigger = iconObj[status];
        const style = styleObj[status];
        if (mode === 'on') {
            style.zIndex = 3;
        }

        const node = (
            <div
                onClick={(e) => handleChangeCollapsed(e, !collapsed)}
                style={{...style, ...triggerStyle}}
                className={classNames(
                    `${clsPrefix}-resizer-trigger`,
                    `${clsPrefix}-resizer-trigger-${collapsed ? 'collapsed' : 'expanded'}`,
                    `${clsPrefix}-resizer-trigger-${collapsed ? 'collapsed' : 'expanded'}-${dir}`,
                )}
                fieldid={fieldid ? `${fieldid}_resizer_trigger` : undefined}
            >
                {
                    typeof (trigger) === 'string' ?
                        <Icon type={trigger} />
                        :
                        (trigger || defaultTrigger)
                }
            </div>
        );
        let placement = {
            vertical: verticalReverseArrow ? collapsed ? 'left' : 'right' : 'left',
            horizontal: 'top',
        };

        return trigger !== null ? (
            <Tooltip key={String(collapsed)} mouseLeaveDelay={0} overlay={collapsed ? langInfo?.langMap.unfold : langInfo?.langMap.fold} placement={placement[direction] as TooltipPlacement}>
                {node}
            </Tooltip>
        ) : null;
    }, [direction, primary, dir, collapsed, mode, clsPrefix, fieldid, trigger, triggerStyle, handleChangeCollapsed]);

    const getFixeNailDom = useCallback(() => {
        const reverseArrow = primary === 'second';
        const isRTL = dir === 'rtl';
        const styleObj = reverseArrow ? {[isRTL ? "right" : "left"]: 'unset', [isRTL ? "left" : "right"]: '0px', borderRadius: isRTL ? ' 0 50% 50% 0' : '50% 0 0 50%'} : {[isRTL ? "right" : "left"]: '0px', [isRTL ? "left" : "right"]: 'unset', borderRadius: isRTL ? ' 50% 0 0 50%' : '0 50% 50% 0'};
        return modeProps === 'mixed' && !collapsed && direction === 'vertical' ? (
            <Tooltip overlay={mode === 'on' ? (langInfo?.langMap.fixed || '固定侧边栏') : (langInfo?.langMap.unfix || '取消固定')} placement={reverseArrow ? 'left' : 'right'}>
                <div
                    style={{...styleObj}}
                    onClick={handleFixedNailClick}
                    className={classNames(
                    `${clsPrefix}-resizer-fixed`
                    )}
                    fieldid={fieldid ? `${fieldid}_resizer_fixed` : undefined}
                >
                    {mode === 'on' ? <Icon type="uf-suoding" /> : <Icon type="uf-dingzhu" />}
                </div>
            </Tooltip>
        ) : null;
    }, [primary, dir, modeProps, collapsed, direction, mode, clsPrefix, fieldid, handleFixedNailClick]);

    const style: React.CSSProperties = useMemo(() => {
        const baseStyle: React.CSSProperties = {
            display: 'flex',
            flex: 1,
            height: '100%',
            position: 'relative',
            outline: 'none',
            overflow: 'hidden',
            MozUserSelect: 'text',
            WebkitUserSelect: 'text',
            msUserSelect: 'text',
            userSelect: 'text',
            ...(styleProps || {}),
        };

        if (direction === 'vertical') {
            Object.assign(baseStyle, {
                flexDirection: mode === 'on' && primary === 'second' ? 'row-reverse' : 'row',
                left: 0,
                right: 0,
            });
        } else if (direction === 'horizontal') {
            Object.assign(baseStyle, {
                bottom: 0,
                flexDirection: 'column',
                minHeight: '100%',
                top: 0,
                width: '100%',
            });
        }
        return baseStyle;
    }, [styleProps, direction, mode, primary]);

    const paneFirstStyle: React.CSSProperties = {
        zIndex: collapsed ? 1 : 2
    };
    const paneSecondStyle: React.CSSProperties = {
        position: 'absolute',
        height: "100%",
        width: "100%",
        zIndex: 1
    };
    const classes = classNames(clsPrefix, {
        [`${clsPrefix}-disabled`]: !allowResize,
        [`${clsPrefix}-collapsed`]: collapsible && !active, // 增加collapsed 动画
    }, className);
    const pane1Classes = classNames(`${clsPrefix}-first`, {
        [`${clsPrefix}-first-hidden`]: collapsed && mode !== 'on' && primary === 'first'
    });
    const pane2Classes = classNames(`${clsPrefix}-second`, {
        [`${clsPrefix}-first-hidden`]: collapsed && mode !== 'on' && primary === 'second'
    });
    const triggerDom = getTriggerDom();
    const fixeNailDom = getFixeNailDom();
    let adapterNid = getNid(props); // 适配nid、uitype

    let resizerRootClassName = classNames({
        [`${clsPrefix}-resizer-primary`]: mode === 'on' && primary === 'second',
    });

    return (
        <div
            className={classes}
            ref={spliterRef}
            style={style}
            {...adapterNid}
            fieldid={fieldid}
        >
            <Pane
                className={pane1Classes}
                key="pane1"
                domKey={`pane1${pane1Key.current}`}
                primary={primary}
                ref={pane1Ref}
                style={mode === 'on' ? (primary === 'first' ? paneFirstStyle : paneSecondStyle) : {}}
                size={
                    primary === 'first' ? size ?? defaultSize ?? minSize : undefined
                }
                direction={direction}
                fieldid={fieldid ? `${fieldid}_pane1` : undefined}
            >
                {children && children.length > 0 ? children[0] : null }
            </Pane>
            <Resizer
                onClick={onResizerClick}
                onDoubleClick={onResizerDoubleClick}
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}
                onTouchEnd={onMouseUp}
                key="resizer"
                ref={resizerRef}
                resizerClassName={resizerClassName}
                direction={direction}
                primary={primary}
                resizerable={resizerable}
                style={resizerStyle}
                collapsed={collapsed}
                trigger={collapsible ? triggerDom : null}
                fixeNail={collapsible ? fixeNailDom : null}
                className={resizerRootClassName}
                fieldid={fieldid ? `${fieldid}_resizer` : undefined}
            />
            <Pane
                className={pane2Classes}
                style={mode === 'on' ? (primary === 'second' ? paneFirstStyle : paneSecondStyle) : {}}
                key="pane2"
                domKey={`pane2${pane2Key.current}`}
                primary={primary}
                ref={pane2Ref}
                size={
                    primary === 'second' ? size ?? defaultSize ?? minSize : undefined
                }
                direction={direction}
                fieldid={fieldid ? `${fieldid}_pane2` : undefined}
            >
                {
                    React.Children.map(children, (child, index) => {
                        if (index === 0) return null;
                        return child;
                    })
                }
            </Pane>
        </div>
    );
};

export default WithConfigConsumer({name: "spliter"})(WebUI({name: "spliter", defaultProps})(Spliter)) as React.ComponentClass<Partial<SpliterProps>>;